import type { ReactNode } from 'react';
import './tacticsHud.css';

interface HudShellProps {
  children: ReactNode;
  /**
   * Optional polite live-region message. Used by the 2D board to
   * announce selection / nudge events; the 3D board can pass `null`
   * if it doesn't need announcements.
   */
  liveMessage?: string | null;
}

/**
 * Bottom-anchored HUD wrapper. Owns the absolute positioning, column
 * stack, max-width container, and the screen-reader live region. Each
 * concrete board renders a single <HudShell> with one <HudRow> per
 * cluster of controls inside.
 *
 * `pointer-events: none` on the shell with `pointer-events: auto` on
 * each row lets clicks fall through the dead space between rows so the
 * board surface stays draggable behind the HUD chrome.
 */
export function HudShell({ children, liveMessage }: HudShellProps) {
  return (
    <div className="thud">
      {children}
      {/* aria-live region — placed inside the HUD shell so it inherits
          the shell's stacking context without affecting the visual
          layout (sr-only off-screens it). aria-atomic so the whole
          message reads even if only a substring changed (selection
          → cleared) between announcements. */}
      <div
        className="thud-sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="thud-live"
      >
        {liveMessage ?? ''}
      </div>
    </div>
  );
}
